import type { RoundSnapshot } from "../../contracts/RoundSnapshot";
import type { ReplayConfiguration } from "./ReplayConfiguration";
import { createDefaultReplayConfiguration } from "./ReplayConfiguration";
import type { ReplayContext } from "./ReplayContext";
import { ReplayCancellationToken } from "./ReplayCancellationToken";
import { ReplayCancelledError, UnknownActionTypeError, UnknownEventTypeError } from "../errors/ReplayErrors";
import { EventHandlerRegistry } from "../dispatch/EventHandlerRegistry";
import { ActionHandlerRegistry } from "../dispatch/ActionHandlerRegistry";
import type { EventObserver, FramePresenter } from "../plugins/FramePresenter";
import { hasGridFrameContent, isFeatureEnterExitEventTypeName } from "../dispatch/EventTypeNamePatterns";
import { isCanonicalActionTypeName } from "../../shared/CanonicalActionTypeNames";
import type { RoundReplayResult } from "./RoundReplayResult";
import { resolveActionPhaseName } from "../policy/ActionPhaseResolver";

/**
 * Deterministic, sequential, awaited replay engine.
 * Interprets a RoundSnapshot exactly as provided by the server.
 */
export class RoundReplayEngine {
  private readonly eventHandlerRegistry: EventHandlerRegistry;
  private readonly actionHandlerRegistry: ActionHandlerRegistry;

  public constructor(eventHandlerRegistry: EventHandlerRegistry, actionHandlerRegistry: ActionHandlerRegistry) {
    this.eventHandlerRegistry = eventHandlerRegistry;
    this.actionHandlerRegistry = actionHandlerRegistry;
  }

  /**
   * Plays a full round snapshot deterministically through the frame presenter, the observers (in
   * order) and the registered action / event handlers. Returns a stable RoundReplayResult.
   */
  public async playRoundSnapshot(
    roundSnapshot: RoundSnapshot,
    framePresenter: FramePresenter,
    observers: readonly EventObserver[],
    configuration?: Partial<ReplayConfiguration>,
    logDebugMessage?: (message: string) => void
  ): Promise<RoundReplayResult> {
    const mergedConfiguration: ReplayConfiguration = {
      ...createDefaultReplayConfiguration(),
      ...(configuration ?? {}),
    };

    const cancellationToken = new ReplayCancellationToken();

    const context: ReplayContext = {
      roundIdentifier: roundSnapshot.roundIdentifier,
      currentEventIndex: -1,
      currentActionIndex: null,
      configuration: mergedConfiguration,
      cancellationToken,
      logDebugMessage,
      finalWinAmount: undefined,
      activeSpinBoundaryIdentifier: null,
      activeSpinModeIdentifier: null,
    };

    await this.playRoundSnapshotInternal(roundSnapshot, framePresenter, observers, context);

    return {
      roundIdentifier: context.roundIdentifier,
      finalWinAmount: context.finalWinAmount ?? "0",
    };
  }

  private async playRoundSnapshotInternal(
    roundSnapshot: RoundSnapshot,
    framePresenter: FramePresenter,
    observers: readonly EventObserver[],
    context: ReplayContext
  ): Promise<void> {
    for (let eventIndex = 0; eventIndex < roundSnapshot.orderedEvents.length; eventIndex++) {
      context.currentEventIndex = eventIndex;
      context.currentActionIndex = null;
      context.currentActionPhaseName = "other";
  
      this.throwIfCancelled(context);
  
      const roundEvent = {
        ...roundSnapshot.orderedEvents[eventIndex],
        roundIdentifier: roundSnapshot.roundIdentifier,
      };
      
      this.applyFeatureStateFromEvent(roundEvent, context);
  
      context.logDebugMessage?.(
        `[Replay] Event ${eventIndex}/${roundSnapshot.orderedEvents.length - 1}: ${roundEvent.eventTypeName}`
      );
  
      for (const observer of observers) {
        if (observer.onEventStart) await observer.onEventStart(roundEvent, context);
      }

      // ------------------------------------------------------------
      // Option A: Decode the grid snapshot first (non-animated).
      // This allows frame_prepare actions to bind to SymbolViews that already exist.
      // ------------------------------------------------------------
      if (framePresenter.prepareEventFrame) {
        await framePresenter.prepareEventFrame(roundEvent, context);
      }
  
      const actions = roundEvent.eventContent.orderedActions ?? [];
  
      // Split actions by phase tag
      const framePrepareActions: typeof actions = [];
      const remainingActions: typeof actions = [];
  
      for (const a of actions) {
        const phaseName = resolveActionPhaseName(a);
        if (phaseName === "frame_prepare") framePrepareActions.push(a);
        else remainingActions.push(a);
      }

      const runActionsSequentially = async (subset: typeof actions, _subsetLabel: string): Promise<void> => {
        for (let actionIndex = 0; actionIndex < subset.length; actionIndex++) {
          context.currentActionIndex = actionIndex;
  
          this.throwIfCancelled(context);
  
          const roundAction = subset[actionIndex];
          const phaseName = resolveActionPhaseName(roundAction);
          context.currentActionPhaseName = phaseName;

          context.logDebugMessage?.(
            `  [Replay] Action ${actionIndex}/${subset.length - 1}: ${roundAction.actionTypeName} (phase: ${phaseName})`
          );
  
          // Per-phase skipping (only ever drops actions marked `isSkippable`). When a
          // `actionPhaseSkipPolicy` is provided, a phase is skipped only if its entry is
          // `true` — so e.g. super_turbo can skip the filler phases while KEEPING the
          // `win` phase. With no policy, fall back to the blunt `shouldSkipAnimations`.
          const phaseSkipPolicy = context.configuration.actionPhaseSkipPolicy;
          const shouldSkipThisAction = phaseSkipPolicy
            ? phaseSkipPolicy[phaseName] === true
            : context.configuration.shouldSkipAnimations;

          if (shouldSkipThisAction && roundAction.isSkippable === true) {
            context.logDebugMessage?.(
              `  [Replay] Skipped action ${roundAction.actionTypeName} (phase: ${phaseName})`
            );
            continue;
          }
  
          const actionHandlers = this.actionHandlerRegistry.getHandlers(roundAction.actionTypeName);

          if (actionHandlers.length === 0) {
            // A canonical action nobody presents in this game: a logged skip (plan §2.7).
            if (isCanonicalActionTypeName(roundAction.actionTypeName)) {
              context.logDebugMessage?.(
                `  [Replay] No plugin presents ${roundAction.actionTypeName}; skipped`
              );
              continue;
            }

            const canSkipUnknown =
              !context.configuration.shouldFailOnUnknownTypes && roundAction.isSkippable === true;
  
            if (canSkipUnknown) {
              context.logDebugMessage?.(
                `  [Replay] Skipped unknown action ${roundAction.actionTypeName} (skippable)`
              );
              continue;
            }
  
            throw new UnknownActionTypeError(roundAction.actionTypeName);
          }
  
          for (const actionHandler of actionHandlers) {
            await actionHandler.handleAction(roundAction, context);
          }
        }
  
        // Reset action cursor after a subset
        context.currentActionIndex = null;
        context.currentActionPhaseName = "other";
      };
  
      // ------------------------------------------------------------
      // 1) frame_prepare actions BEFORE the animated frame
      // ------------------------------------------------------------
      await runActionsSequentially(framePrepareActions, "frame_prepare");
  
      this.throwIfCancelled(context);
  
      // ------------------------------------------------------------
      // 2) The animated frame: a plugin's event handler if one claims the event, else the default
      //    frame step — feature state to the observers, then the frame presenter's reveal, then the
      //    observers' onFrameRevealed — for any event with grid content or a feature enter / exit.
      // ------------------------------------------------------------
      const eventHandler = this.eventHandlerRegistry.getHandler(roundEvent);

      if (eventHandler) {
        await eventHandler.handleEvent(roundEvent, context);
      } else if (hasGridFrameContent(roundEvent.eventContent) || isFeatureEnterExitEventTypeName(roundEvent.eventTypeName)) {
        if (roundEvent.eventContent.featureState) {
          for (const observer of observers) {
            if (observer.onFeatureState) await observer.onFeatureState(roundEvent, context);
          }
        }
        await framePresenter.revealEventFrame(roundEvent, context);
        for (const observer of observers) {
          if (observer.onFrameRevealed) await observer.onFrameRevealed(roundEvent, context);
        }
      } else if (context.configuration.shouldFailOnUnknownTypes) {
        throw new UnknownEventTypeError(roundEvent.eventTypeName);
      } else {
        context.logDebugMessage?.(
          `  [Replay] No frame for "${roundEvent.eventTypeName}". Skipping animated frame.`
        );
      }

      this.throwIfCancelled(context);
  
      // ------------------------------------------------------------
      // 3) Remaining actions AFTER the animated frame
      // ------------------------------------------------------------
      await runActionsSequentially(remainingActions, "remaining");

      // Record the event's accumulated total after its actions. The round's last event always
      // carries it (decision 26), so the result is the snapshot's total; `update_total_win_meter`
      // is a UI driver and may be absent (`feature_exit` carries no actions).
      if (
        typeof roundEvent.accumulatedWinAmountAfterEvent === "string" &&
        roundEvent.accumulatedWinAmountAfterEvent.length > 0
      ) {
        context.finalWinAmount = roundEvent.accumulatedWinAmountAfterEvent;
      }

      for (const observer of observers) {
        if (observer.onEventEnd) await observer.onEventEnd(roundEvent, context);
      }

      this.clearFeatureStateIfExitEvent(roundEvent, context);
    }
  }

  private applyFeatureStateFromEvent(
    roundEvent: RoundSnapshot["orderedEvents"][number],
    context: ReplayContext
  ): void {
    const featureState = roundEvent.eventContent.featureState;
  
    if (featureState) {
      const previous = context.featureState;
  
      context.featureState = {
        featureIdentifier: featureState.featureIdentifier,
        featureCountRemaining: featureState.featureCountRemaining,
        featureAccumulatedWinMinor: featureState.featureAccumulatedWinMinor,
        featurePhase: featureState.featurePhase,
        customPayload: featureState.customPayload,
      };
  
      const changed =
        !previous ||
        previous.featureIdentifier !== context.featureState.featureIdentifier ||
        previous.featureCountRemaining !== context.featureState.featureCountRemaining ||
        previous.featureAccumulatedWinMinor !== context.featureState.featureAccumulatedWinMinor ||
        previous.featurePhase !== context.featureState.featurePhase;
  
      if (changed) {
        context.logDebugMessage?.(
          `[Replay] FeatureState: ${context.featureState.featureIdentifier}` +
            `${context.featureState.featurePhase ? ` (${context.featureState.featurePhase})` : ""}` +
            `${context.featureState.featureCountRemaining ? ` count=${context.featureState.featureCountRemaining}` : ""}` +
            `${context.featureState.featureAccumulatedWinMinor ? ` total=${context.featureState.featureAccumulatedWinMinor}` : ""}`
        );
      }
  
      return;
    }
  
    // Some backends emit feature_exit without featureState payload.
    // We handle that via clearFeatureStateIfExitEvent AFTER event ends.
  }
  
  private clearFeatureStateIfExitEvent(
    roundEvent: RoundSnapshot["orderedEvents"][number],
    context: ReplayContext
  ): void {
    const phase = roundEvent.eventContent.featureState?.featurePhase;
    const isExitByPhase = phase === "exit";
    const isExitByName = roundEvent.eventTypeName === "feature_exit";
  
    if (!isExitByPhase && !isExitByName) return;
  
    if (context.featureState) {
      context.logDebugMessage?.(
        `[Replay] FeatureState cleared (event=${roundEvent.eventTypeName}${phase ? ` phase=${phase}` : ""})`
      );
    }
  
    context.featureState = undefined;
  }

  private throwIfCancelled(context: ReplayContext): void {
    if (!context.cancellationToken.isCancelled()) return;
    throw new ReplayCancelledError(context.cancellationToken.getReason() ?? "cancelled");
  }
}
